import React, { useEffect, useRef, useState } from 'react';
import { WebPlayer } from '@antmedia/web_player';
import { Button, IconButton } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import './styles.css';
import PropTypes from 'prop-types';

const Player = ({ sessionId, shouldReplay }) =>
{
	const playerRef = useRef();
	const placeHolderRef = useRef();
	const embeddedPlayerRef = useRef(null);
	const videoElRef = useRef(null);
	const volumeSyncRef = useRef(null);
	const mutedTimeoutsRef = useRef([]);

	const playOrderLocal = [ 'hls' ];
	const [ isPlayerMuted, setPlayerMuted ] = useState(false);
	const [ audioMuted, setAudioMuted ] = useState(false);
	const [ hover, setHover ] = useState(false);

	// New state to trigger replay
	const [ hasUnmuted, setHasUnmuted ] = useState(false);
	// Function to play the video
	const playVideo = async () =>
	{
		const player = embeddedPlayerRef.current;

		if (!player) return;

		try
		{
			// Attempt playback WITH sound. Where autoplay-with-sound is allowed
			// (Chrome on Android/desktop, Safari on iOS with prior engagement) it
			// just plays and no prompt is shown. Where it is blocked — most strictly
			// Chrome on iOS (WKWebView) — the antmedia player catches the
			// NotAllowedError internally and falls back to muted playback. We detect
			// exactly that case below via isMuted(), so the "tap to enable sound"
			// overlay appears ONLY on browsers that actually muted us, not everywhere.
			await player.play();
		}
		catch (error)
		{
			console.error(`Error while playing video: ${error}`);
		}
		finally
		{
			syncMutedState();
			attachMutedListeners();
		}
	};

	// Reflect the REAL muted state of the underlying <video>. When the browser
	// blocks autoplay-with-sound, the antmedia player falls back to muted a beat
	// AFTER play() resolves, so a single check misses it and the overlay never
	// shows. Reading video.muted (and listening for its changes) catches it.
	const syncMutedState = () =>
	{
		const player = embeddedPlayerRef.current;

		if (!player) return;

		const video = videoElRef.current || playerRef.current?.querySelector('video');

		if (video) videoElRef.current = video;

		const muted = video
			? video.muted
			: (typeof player.isMuted === 'function' ? player.isMuted() : false);

		setAudioMuted(muted);
		setPlayerMuted(muted);
	};

	// Listen on the <video> so any mute (incl. the autoplay-block fallback that
	// lands late) drives the overlay, plus a few timed re-checks as a backstop.
	const attachMutedListeners = () =>
	{
		const video = playerRef.current?.querySelector('video');

		if (video && !volumeSyncRef.current)
		{
			videoElRef.current = video;
			const handler = () => syncMutedState();

			volumeSyncRef.current = handler;
			video.addEventListener('volumechange', handler);
			video.addEventListener('play', handler);
		}

		mutedTimeoutsRef.current.forEach(clearTimeout);
		mutedTimeoutsRef.current = [ 300, 1000, 2500 ].map((t) => setTimeout(syncMutedState, t));
	};

	// Function to initialize the player
	const initializePlayer = () =>
	{
		if (playerRef && playerRef.current)
		{
			embeddedPlayerRef.current = new WebPlayer({
				streamId         : sessionId,
				httpBaseURL      : import.meta.env.VITE_streaming,
				videoHTMLContent : '<video id="video-player" class="video-js vjs-default-skin vjs-big-play-centered" controls playsinline style="width:100%;height:100%;display:flex;"></video>',
				playOrder        : playOrderLocal
			}, playerRef.current, placeHolderRef.current);

			embeddedPlayerRef.current.initialize()
				.then(() =>
				{
					console.log('Player initialized');
					playVideo();
				})
				.catch((error) =>
				{
					console.error(`Error while initializing embedded player: ${error}`);
				});
		}
	};

	// Initialize player when component mounts or sessionId changes
	useEffect(() =>
	{
		console.log('asdjhsad sadasdsa dasjd ');
		initializePlayer();
		// Cleanup function to destroy player when component unmounts

		return () =>
		{
			mutedTimeoutsRef.current.forEach(clearTimeout);
			mutedTimeoutsRef.current = [];

			const video = videoElRef.current;

			if (video && volumeSyncRef.current)
			{
				video.removeEventListener('volumechange', volumeSyncRef.current);
				video.removeEventListener('play', volumeSyncRef.current);
			}
			volumeSyncRef.current = null;
			videoElRef.current = null;

			if (embeddedPlayerRef.current)
			{
				embeddedPlayerRef.current.destroy();
				embeddedPlayerRef.current = null;
			}
		};
	}, [ sessionId ]);

	// // Effect to handle replay
	// useEffect(() =>
	// {
	// 	if (shouldReplay)
	// 	{
	// 		setShouldPlay(true);
	// 	}
	// }, [ shouldReplay ]);

	// // Effect to play video when shouldPlay is true
	// useEffect(() =>
	// {
	// 	if (shouldPlay)
	// 	{
	// 		playVideo();
	// 		setShouldPlay(false);
	// 	}
	// }, [ shouldPlay ]);

	const handleUnmute = async () =>
	{
		const player = embeddedPlayerRef.current;

		if (!player) return;

		player.mutePlayer(false);

		// WKWebView (Chrome on iOS) often needs play() re-invoked inside the same
		// user gesture for the now-unmuted audio to actually start.
		try
		{
			await player.play();
		}
		catch (error)
		{
			console.error(`Error while unmuting: ${error}`);
		}

		setAudioMuted(false);
		setPlayerMuted(false);
		setHover(false);
		setHasUnmuted(true);
	};

	// Let the viewer choose to keep watching muted, while still leaving the small
	// volume toggle available so they can turn sound on later.
	const dismissUnmutePrompt = () =>
	{
		setPlayerMuted(false);
		setHasUnmuted(true);
	};

	const handleMute = () =>
	{
		if (embeddedPlayerRef && embeddedPlayerRef.current)
		{
			embeddedPlayerRef.current.mutePlayer(true);
			setAudioMuted(true);
		}
	};

	const handleMouseEnter = (event) =>
	{
		event.stopPropagation();
		setHover(true);
		setPlayerMuted(embeddedPlayerRef.current.isMuted());
	};

	const handleMouseOut = (event) =>
	{
		event.stopPropagation();
		setHover(false);
	};

	return (
		<div
			style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', flexDirection: 'column' }}
			onMouseLeave={handleMouseOut}
			onMouseEnter={handleMouseEnter}
			onTouchStart={handleMouseEnter}
			onTouchEnd={handleMouseOut}
		>
			<div
				ref={playerRef}
				id='video_container'
				className='flexCol aCntr jCntr fullH fullW pRel videoMuteHide'
				style={{ display: 'flex' }}
			/>
			{/* {(hover && !isPlayerMuted) && (
				<div className='flexCol aCntr jCntr pAbs fullW fullH'>
					<IconButton>
						<div className='flexInline pdngSM brdrRadius50 iconStyles'>
							{embeddedPlayerRef?.current?.mute && audioMuted
								? <VolumeOffIcon onClick={handleUnmute}/>
								: <VolumeUpIcon onClick={handleMute}/>
							}
						</div>
					</IconButton>
				</div>
			)} */}
			{/* <div className='flexInline pAbs iconstyles pdngSM'>
				<IconButton size='small' >
					<div className='flexInline pdngSM brdrRadius50 iconStyles'>
						{embeddedPlayerRef?.current?.mute && audioMuted
							? <VolumeOffIcon fontSize='small' onClick={handleUnmute} />
							: <VolumeUpIcon fontSize='small' onClick={handleMute} />
						}
					</div>
				</IconButton>
			</div>
			{isPlayerMuted && (
				<div className='flexRow aCntr jCntr fullW pAbs absStyles'>
					<div className='flexAutoRow playerPdng whiteBg brdrRadiusSM'>
						<div className='flexMinWidthRow aCntr jCntr brdrRSM pdngRSM'>
							<InfoRoundedIcon fontSize='small'/>
							<small className='pdngHSM'>Not hearing anything?</small>
							<Button
								disableElevation
								color='primary'
								variant='contained'
								style={{ fontSize: '10px' }}
								size='small'
								onClick={handleUnmute}
							>
								unmute
							</Button>
						</div>
						<div
							className='flexAutoRow aCntr jCntr playerPdng pointer'
							role='presentation'
							onClick={() => setPlayerMuted(false)}
						>
							<CloseRoundedIcon fontSize='small'/>
						</div>
					</div>
				</div>
			)} */}

			{/* Speaker on/off toggle — always available once the player exists. */}
			<div className='flexInline pFix iconstyles'>
				<IconButton size='small'>
					<div className='flexInline pdngXS brdrRadius50 iconStyles'>
						{audioMuted
							? <VolumeOffIcon fontSize='small' onClick={handleUnmute} />
							: <VolumeUpIcon fontSize='small' onClick={handleMute} />
						}
					</div>
				</IconButton>
			</div>

			{/* Full-cover prompt — shown ONLY when this browser actually blocked
				autoplay-with-sound (isPlayerMuted set from isMuted() above), and only
				until the viewer unmutes or dismisses it. A small icon alone is too
				easy to miss, which reads as "the audio is broken". */}
			{isPlayerMuted && !hasUnmuted && (
				<div
					className='flexCol aCntr jCntr fullW fullH pAbs absStyles tapUnmuteOverlay pointer'
					role='presentation'
					onClick={handleUnmute}
				>
					<div className='flexInline pdngSM brdrRadius50 iconStyles'>
						<VolumeOffIcon />
					</div>
					<span className='pdngTSM tapUnmuteText'>Tap to enable sound</span>
					<Button
						disableElevation
						color='primary'
						variant='contained'
						style={{ fontSize: '10px', marginTop: '8px' }}
						size='small'
						onClick={handleUnmute}
					>
						unmute
					</Button>
					<span
						className='pdngTSM tapUnmuteDismiss pointer'
						role='presentation'
						onClick={(e) =>
						{
							e.stopPropagation();
							dismissUnmutePrompt();
						}}
					>
						Continue watching muted
					</span>
				</div>
			)}
			<div
				ref={placeHolderRef}
				className='placeholder'
				style={{
					height         : '360px',
					overflow       : 'hidden',
					display        : 'flex',
					alignItems     : 'center',
					justifyContent : 'center',
					color          : '#ffffff'
				}}
			>
				The streaming will begin shortly...
			</div>
		</div>
	);
};

Player.propTypes = {
	sessionId    : PropTypes.string.isRequired,
	shouldReplay : PropTypes.bool
};

export default Player;